import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { salesService } from '../../services/sales.service';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';
import LogicContext from '../../Context/LogicContext';
import { SetAddressStoreAtom, GetAddressStoreAtom } from '../../store/addressStore';


interface Area {
  area_id: number | string;
  area_name: string;
  city_id?: number | string;
  country_id?: number | string;
}

interface City {
  city_id: number | string;
  city_name: string;
  country_id?: number | string;
}

interface Country {
  country_id: number | string;
  country_name: string;
}

interface AddressInfoType {
  country: string;
  city: string;
  area: string;
  addressLine: string;
  zip_code: string;
}

interface ModalProps {
  isOpen: boolean;
  saleId: string;
  defaultData?: AddressInfoType;
  onClose: () => void;
  reload: () => void;
}

export interface LocationData {
  area: Area[];
  city: City[];
  country: Country[];
}

export default function SalesOrderAddressModal({ saleId, isOpen, defaultData, onClose, reload }: ModalProps) {
  const {
    register, handleSubmit, formState: { errors }, setValue, watch, reset,
  } = useForm<AddressInfoType>();
  const addressStore =  GetAddressStoreAtom();
  const setAddressStore = SetAddressStoreAtom();

  const logicContext = useContext(LogicContext);

  // Watch form values for cascade filtering
  const watchCountry = watch('country');
  const watchCity = watch('city');

  // Load data
  useEffect(() => {
    if (!isOpen) return;
    if (!addressStore?.country?.length && saleId) {
      salesService.salesOrderShippingOrder(saleId).then(res => {
        const serverData = toLowercaseKeys(res?.data);
        setAddressStore(serverData);
      });
    }
 
  }, [saleId, isOpen]);

  useEffect(() => {
    if(defaultData?.country){
      setValue('country', defaultData.country);
      setValue('city', defaultData.city);
      setValue('area', defaultData.area);
      setValue('addressLine', defaultData.addressLine);
    }
  }, [defaultData]);



  // Reset dependent fields when country changes
  useEffect(() => {
    if (watchCountry) {
      setValue('city', '');
      setValue('area', '');
    }
  }, [watchCountry, setValue]);

  // Reset area when city changes
  useEffect(() => {
    if (watchCity) {
      setValue('area', '');
    }
  }, [watchCity, setValue]);

  const submitForm = async (data: AddressInfoType) => {
    const formData = {
      sales_order_ref_no: saleId,
      address1: data?.addressLine,
      area_id: data?.area,
      zip_code: data.zip_code,
    };

    const res = await salesService.submitSalesOrderAddress(formData);
    if (res?.data !== undefined) {
      const cleanResponse: any = logicContext.httpResponseManager(res.data?.[0] || res.data?.result_status?.[0]);
      const { message_id, message_text } = cleanResponse;

      if (message_id == 1) {
        logicContext.showToast(message_text, 'success');
        onClose();
        reload()
      }
    }
  };

  if (!isOpen) return null;

  // Filter cities by selected country
  const filteredCities = addressStore?.city?.filter(c => String(c.country_id) === watchCountry) || [];
  const citiesToShow = filteredCities.length > 0 ? filteredCities : addressStore.city || [];
  

  // Filter areas by selected city
  const filteredAreas = addressStore?.area?.filter(a => String(a.city_id) === watchCity) || [];
    const areaToShow = filteredAreas.length > 0 ? filteredAreas : addressStore.area || [];

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-lg font-semibold text-gray-800">Shipping Address</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
            ✕
          </button>
        </div>

        <form
          onSubmit={e => {
            e.stopPropagation();
            handleSubmit(submitForm)(e);
          }}
          className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Country</label>
            <select
              {...register('country', { required: true })}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Select Country</option>
              {addressStore?.country?.map(c => (
                <option key={c.country_id} value={String(c.country_id)}>
                  {c.country_name}
                </option>
              ))}
            </select>
            {errors.country && <span className="text-xs text-red-600">Country is required</span>}
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700">City</label>
            <select
              {...register('city', { required: true })}
              disabled={!watchCountry}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
            >
              <option value="">Select City</option>
              {citiesToShow.map(c => (
                <option key={c.city_id} value={String(c.city_id)}>
                  {c.city_name}
                </option>
              ))}
            </select>
            {errors.city && <span className="text-xs text-red-600">City is required</span>}
          </div>

          {/* Area */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Area</label>
            <select
              {...register('area', { required: true })}
              disabled={!watchCity}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
            >
              <option value="">Select Area</option>
              {areaToShow.map(a => (
                <option key={a.area_id} value={String(a.area_id)}>
                  {a.area_name}
                </option>
              ))}
            </select>
            {errors.area && <span className="text-xs text-red-600">Area is required</span>}
          </div>

          {/* Address Line */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Address Line</label>
            <input
              type="text"
              {...register('addressLine', { required: true })}
              placeholder="Enter address line/landmark"
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            {errors.addressLine && <span className="text-xs text-red-600">Address line is required</span>}
          </div>

          {/* Zip Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Zip code(Optional)</label>
            <input
              type="text"
              {...register('zip_code')}
              placeholder="Enter address zip code"
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            {errors.zip_code && <span className="text-xs text-red-600">Zip code is required</span>}
          </div>

          {/* Buttons */}
          <div className="col-span-full mt-4 flex justify-end border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 rounded-lg border border-gray-300 px-4 py-2 text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button type="submit" className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
